import { useQuery } from '@tanstack/react-query';
import { Clock, AlertCircle, Mail, Building2 } from 'lucide-react';
import { emailsApi } from '../lib/api';
import { Card, CardHeader, CardTitle, CardContent } from './Card';

interface AwaitingContact {
  id: string;
  name: string;
  email: string | null;
  company: string | null;
  last_email_at: string;
  email_count: number;
  days_waiting: number;
  last_email_subject: string | null;
  urgency: 'low' | 'medium' | 'high' | 'critical';
}

interface AwaitingResponseListProps {
  onContactClick?: (contactId: string) => void;
  limit?: number;
}

const urgencyConfig = {
  low: {
    icon: Clock,
    className: 'text-[var(--asi-text-secondary)]',
    bgClassName: 'bg-[var(--asi-bg-tertiary)]',
    label: 'Recent',
  },
  medium: {
    icon: Clock,
    className: 'text-[#D97706]',
    bgClassName: 'bg-[#FEF3C7]',
    label: '1-3 days',
  },
  high: {
    icon: AlertCircle,
    className: 'text-[#DC2626]',
    bgClassName: 'bg-[#FEE2E2]',
    label: '3-7 days',
  },
  critical: {
    icon: AlertCircle,
    className: 'text-[#B91C1C]',
    bgClassName: 'bg-[#FEE2E2]',
    label: '7+ days',
  },
};

export function AwaitingResponseList({ onContactClick, limit = 10 }: AwaitingResponseListProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['emails', 'awaiting-response', limit],
    queryFn: () => emailsApi.awaitingResponse(limit),
    refetchInterval: 60000, // Refresh every minute
  });

  const contacts = (data?.contacts || []) as AwaitingContact[];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Awaiting Response</CardTitle>
          {contacts.length > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-[#FEF3C7] text-[#D97706] rounded-full">
              {contacts.length}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--asi-accent)]" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-8 text-[var(--asi-text-secondary)]">
            <Mail className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">All caught up!</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--asi-bg-tertiary)]">
            {contacts.map((contact) => {
              const urgency = urgencyConfig[contact.urgency];
              const UrgencyIcon = urgency.icon;

              return (
                <button
                  key={contact.id}
                  onClick={() => onContactClick?.(contact.id)}
                  className="w-full px-4 py-3 text-left hover:bg-[var(--asi-bg-secondary)] transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-1.5 rounded-lg ${urgency.bgClassName}`}>
                      <UrgencyIcon className={`w-4 h-4 ${urgency.className}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-[var(--asi-text)] truncate">
                          {contact.name}
                        </span>
                        <span className={`text-xs font-medium ${urgency.className}`}>
                          {contact.days_waiting}d
                        </span>
                      </div>
                      {contact.company && (
                        <div className="flex items-center gap-1 mt-0.5 text-xs text-[var(--asi-text-secondary)]">
                          <Building2 className="w-3 h-3" />
                          <span className="truncate">{contact.company}</span>
                        </div>
                      )}
                      {contact.last_email_subject && (
                        <p className="mt-1 text-xs text-[var(--asi-text-muted)] truncate">
                          {contact.last_email_subject}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
